'use client';

import { Package } from '@/lib/mock-data';
import { Check, Users, MapPin, Sparkles } from 'lucide-react';
import Link from 'next/link';

interface PackageTierCardProps {
  pkg: Package;
  eventId: string;
  featured?: boolean;
}

export function PackageTierCard({ pkg, eventId, featured = false }: PackageTierCardProps) {
  const isLowAvailability = pkg.availability < 5;
  const tierColors = {
    Diamond: {
      border: 'border-[#B9F2FF]',
      glow: 'shadow-[0_0_30px_rgba(185,242,255,0.3)]',
      badge: 'bg-[#B9F2FF]/20 text-[#B9F2FF]',
      button: 'from-[#B9F2FF] to-[#00D9FF]',
    },
    Platinum: {
      border: 'border-[#D4AF37]',
      glow: 'shadow-[0_0_40px_rgba(212,175,55,0.5)]',
      badge: 'bg-[#D4AF37]/20 text-[#D4AF37]',
      button: 'from-[#D4AF37] to-[#B76E79]',
    },
    Gold: {
      border: 'border-[#FFD700]',
      glow: 'shadow-[0_0_25px_rgba(255,215,0,0.3)]',
      badge: 'bg-[#FFD700]/20 text-[#FFD700]',
      button: 'from-[#FFD700] to-[#FFA500]',
    },
  };

  const colors = tierColors[pkg.tier];

  return (
    <div
      className={`group relative flex flex-col overflow-hidden rounded-xl border-2 bg-gradient-to-br from-black/80 to-black/60 p-8 backdrop-blur-md transition-all duration-500 hover:-translate-y-2 ${colors.border} ${featured ? colors.glow : ''}`}
    >
      {/* Featured Ribbon */}
      {featured && (
        <div className="absolute -right-12 top-6 rotate-45 bg-gradient-to-r from-[#D4AF37] to-[#B76E79] px-12 py-1 text-sm font-bold text-black shadow-lg">
          MOST POPULAR
        </div>
      )}

      {/* Tier Badge */}
      <div className="mb-6">
        <div className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 ${colors.badge}`}>
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-bold">{pkg.tier}</span>
        </div>
      </div>

      {/* Package Name */}
      <h3 className="mb-2 font-serif text-2xl font-bold text-white">{pkg.name}</h3>

      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="font-serif text-5xl font-bold text-white">
            {pkg.priceUSDT.toLocaleString()}
          </span>
          <span className="text-xl text-gray-400">USDT</span>
        </div>
        <p className="mt-1 text-sm text-gray-400">per package</p>
      </div>

      {/* Availability */}
      <div className="mb-6 rounded-lg border border-[#D4AF37]/20 bg-black/40 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Availability</span>
          <span
            className={`font-bold ${isLowAvailability ? 'text-red-400' : 'text-green-400'}`}
          >
            {pkg.availability} left
          </span>
        </div>
        {isLowAvailability && (
          <p className="mt-2 text-xs text-red-400">⚡ Limited availability - book now!</p>
        )}
      </div>

      {/* Package Details */}
      <div className="mb-6 space-y-3 text-sm text-gray-400">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[#D4AF37]" />
          <span>{pkg.seatingZone}</span>
        </div>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[#D4AF37]" />
          <span>Up to {pkg.maxGuests} guests</span>
        </div>
      </div>

      {/* Perks List */}
      <div className="mb-8 flex-1 space-y-3">
        <p className="mb-4 text-sm font-semibold text-[#D4AF37]">What's Included:</p>
        <ul className="space-y-3">
          {pkg.perks.map((perk, index) => (
            <li key={index} className="flex items-start gap-3">
              <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/20">
                <Check className="h-3 w-3 text-[#D4AF37]" />
              </div>
              <span className="text-sm text-gray-300">{perk}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA Button */}
      <Link
        href={`/booking?event=${eventId}&package=${pkg.id}`}
        className={`flex w-full items-center justify-center rounded-lg bg-gradient-to-r py-4 font-bold text-black transition-all duration-300 hover:scale-105 ${colors.button} ${colors.glow}`}
      >
        Select {pkg.tier} Package
      </Link>
    </div>
  );
}
